import React, { useRef, useState } from 'react';
import ImageLightbox from '../../components/ImageLightbox/ImageLightbox';

const TAP_SLOP = 10;                 // px of movement still counted as a tap, not a scroll
const CLICK_AFTER_POINTER_MS = 700;  // window in which a click is the pointerup's own echo

const plain = (e) =>
  !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && (e.button === undefined || e.button === 0);

/**
 * Owns a fullscreen lightbox over `images` and hands out the props that turn any
 * element into a trigger for it.
 *
 *   const { triggerProps, lightbox } = useLightbox({ images, name });
 *   <a {...triggerProps(3)}><img src={images[3]} alt="" /></a>
 *   {lightbox}
 *
 * The trigger stays an <a href>, so no-JS, middle-click and "open in new tab"
 * all still reach the original file.
 *
 * `images` are already-resolved src strings; pages do their own require()s.
 */
export function useLightbox({ images = [], name = '', label }) {
  const [openAt, setOpenAt] = useState(null);
  const tapStart = useRef(null);
  // A timestamp rather than a flag: a suppressed click would leave a flag stuck
  // set and swallow the next keyboard activation.
  const openedAt = useRef(0);

  // Opening is driven by pointerup rather than click: on touch the browser only
  // synthesises a click once it has ruled out a double-tap, and drops it
  // altogether when two taps land close together - which made re-opening the
  // same image silently fail.
  const triggerProps = (index) => ({
    href: images[index],
    target: '_blank',
    rel: 'noopener noreferrer',
    'aria-label': label ? label(index) : `${name} ${index + 1}`,
    onPointerDown: (e) => {
      tapStart.current = plain(e) ? { id: e.pointerId, x: e.clientX, y: e.clientY } : null;
    },
    onPointerUp: (e) => {
      const start = tapStart.current;
      tapStart.current = null;
      if (!start || start.id !== e.pointerId || !plain(e)) return;
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > TAP_SLOP) return;  // a drag, not a tap
      openedAt.current = e.timeStamp || Date.now();
      setOpenAt(index);
    },
    onPointerCancel: () => { tapStart.current = null; },
    onClick: (e) => {
      if (!plain(e)) return;                  // let the browser follow the href
      e.preventDefault();
      if ((e.timeStamp || Date.now()) - openedAt.current < CLICK_AFTER_POINTER_MS) return;
      setOpenAt(index);                       // keyboard Enter, which fires no pointer events
    },
  });

  const lightbox = openAt === null ? null : (
    <ImageLightbox
      images={images}
      index={openAt}
      name={name}
      onIndexChange={setOpenAt}
      onClose={() => setOpenAt(null)}
    />
  );

  return { triggerProps, lightbox };
}

export default useLightbox;
